import { gaPushEvent } from "../base.analytics";

enum Event {
  PRACTICE_PROFILE_RAIL_VIEWED = "practice_profile_rail_viewed",
  PRACTICE_PROFILE_RAIL_ARTICLE_CLICKED = "practice_profile_rail_article_clicked",
}

/**
 * Which corpus the practice feed was reading — the `All practices` aggregate, or
 * one named practice.
 *
 * Sent as two properties rather than one nullable uid, and the keys are spread
 * CONDITIONALLY: `practice_profile_uid` must be ABSENT on the aggregate, not
 * null, or Mixpanel's "is set" filters match every rail view in the product.
 */
export type PracticeRailCorpusProps =
  { corpus: "all" } | { corpus: "practice"; practice_profile_uid: string };

/**
 * The practice rail reached the screen.
 *
 * The DENOMINATOR of the rail's own funnel, and the half the server cannot
 * supply: the five `practice_rail_*` events emitted by the API count the rail
 * SERVED — a response was built — which is not the same fact as a reader seeing
 * one. Reading them as engagement is the trap this pair exists to close.
 *
 * Fires once per corpus the reader lands on, not once per render and not once
 * per chip: a MeSH chip narrows the rail, it does not replace it.
 */
export function gaEventPracticeProfileRailViewed(
  corpus: PracticeRailCorpusProps
) {
  gaPushEvent(Event.PRACTICE_PROFILE_RAIL_VIEWED, { ...corpus });
}

/**
 * A reader opened a publication FROM the practice rail.
 *
 * The NUMERATOR. Until this existed the practice surface reported impressions
 * and no interaction of any kind, so its interaction rate measured the adoption
 * of the rail and read as disengagement.
 *
 * Distinct from `view_item`, which fires on the DESTINATION article page and is
 * stamped `item_list_name: "article_page"` — the page it fired on, not the rail
 * the reader came from. Its GA4 reverse-ETL therefore files every article open
 * in the product under the `feed` surface, whatever opened it, so it can never
 * say that this arm's readers opened anything.
 */
export function gaEventPracticeProfileRailArticleClicked(
  payload: PracticeRailCorpusProps & { articleId: string }
) {
  const { articleId, ...corpus } = payload;
  gaPushEvent(Event.PRACTICE_PROFILE_RAIL_ARTICLE_CLICKED, {
    ...corpus,
    content_id: articleId,
  });
}
